import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import StoryProgress from './StoryProgress';
import StoryHeader from './StoryHeader';
import StoryMedia from './StoryMedia';
import { storiesAPI } from '../../../services/api';
import ReportModal from '../ReportModal';
import StoryViewersPanel from './StoryViewersPanel';
import StoryReactionsPanel from './StoryReactionsPanel';

const StoryViewer = ({
    stories = [],
    initialStoryIndex = 0,
    currentUser, // The logged-in user
    onClose,
    onDeleteSuccess,
    onStoryViewed, // Callback to notify parent of viewed stories
    viewedStoryIds: initialViewedStoryIds = new Set(), // Viewed story IDs passed from parent
}) => {
    const { t } = useTranslation();

    // -- State --
    const [currentGroupIndex, setCurrentGroupIndex] = useState(0);
    const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
    const [progress, setProgress] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [videoDuration, setVideoDuration] = useState(5000); // Default 5s

    // Interactions
    const [isPressed, setIsPressed] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    const [isMediaLoaded, setIsMediaLoaded] = useState(false); // New state to track media load
    const [isMuted, setIsMuted] = useState(false); // New state for audio control
    const [isDeleting, setIsDeleting] = useState(false); // Track deletion for smooth transition
    const [viewedStoryIds, setViewedStoryIds] = useState(new Set(initialViewedStoryIds)); // Track viewed story IDs (initialize from props)
    const [showViewersPanel, setShowViewersPanel] = useState(false); // Story viewers panel
    const [showReactionsPanel, setShowReactionsPanel] = useState(false); // Story reactions panel
    const [likedStories, setLikedStories] = useState({}); // map of storyId -> boolean
    const [reactionCounts, setReactionCounts] = useState({}); // map of storyId -> count
    const [viewCounts, setViewCounts] = useState({}); // map of storyId -> count

    // -- Refs --
    const progressInterval = useRef(null);
    const pressStartTime = useRef(0);
    const storyContainerRef = useRef(null);

    // -- Grouping Logic (Facebook-style) --
    // Group flat list of stories by userId
    const groupedStories = React.useMemo(() => {
        const groups = [];
        let currentGroup = null;

        stories.forEach((story) => {
            // Skip "Add Story" placeholder if present
            if (story.user?.isAddStory) return;

            const userId = story.user?.id || story.user?.name;

            // Handle nested 'stories' array from backend structure or flat list
            const subStories = (story.stories && Array.isArray(story.stories)) ? story.stories : [story];

            subStories.forEach(sub => {
                // Ensure user object is present on sub-story
                const storyUser = sub.user || story.user;
                const storyUserId = storyUser?.id || storyUser?.name;

                if (!currentGroup || currentGroup.userId !== storyUserId) {
                    currentGroup = {
                        userId: storyUserId,
                        user: storyUser,
                        stories: []
                    };
                    groups.push(currentGroup);
                }
                currentGroup.stories.push({ ...sub, user: storyUser });
            });
        });

        // Sort stories within each group from oldest to newest
        groups.forEach(group => {
            group.stories.sort((a, b) => {
                const dateA = a.rawCreatedAt ? new Date(a.rawCreatedAt) : (a.created_at ? new Date(a.created_at) : new Date(0));
                const dateB = b.rawCreatedAt ? new Date(b.rawCreatedAt) : (b.created_at ? new Date(b.created_at) : new Date(0));
                return dateA.getTime() - dateB.getTime(); // Ascending order (oldest first)
            });
        });

        return groups;
    }, [stories]);

    // -- Initialization --
    useEffect(() => {
        if (groupedStories.length === 0) return;

        // Find the group and story index corresponding to initialStoryIndex from props
        const target = stories[initialStoryIndex];
        if (target) {
            // If target is a group object (has its own stories array), use its first story's ID for lookup
            const targetId = target.stories ? target.stories[0]?.id : target.id;

            let found = false;
            for (let g = 0; g < groupedStories.length; g++) {
                const gStories = groupedStories[g].stories;
                for (let s = 0; s < gStories.length; s++) {
                    // Use String comparison for ID safety
                    if (String(gStories[s].id) === String(targetId)) {
                        // Found the target story group
                        // Now find the first unviewed story in this group, starting from the target story
                        let firstUnviewedIndex = s;
                        for (let i = s; i < gStories.length; i++) {
                            const storyId = gStories[i].id || gStories[i].storyId;
                            const isViewed = gStories[i].has_viewed ||
                                gStories[i].is_viewed ||
                                viewedStoryIds.has(storyId);
                            if (!isViewed) {
                                firstUnviewedIndex = i;
                                break;
                            }
                        }
                        setCurrentGroupIndex(g);
                        setCurrentStoryIndex(firstUnviewedIndex);
                        found = true;
                        break;
                    }
                }
                if (found) break;
            }
        }
    }, [groupedStories, initialStoryIndex, stories, viewedStoryIds]);


    const activeGroup = groupedStories[currentGroupIndex];
    const activeStory = activeGroup?.stories[currentStoryIndex];

    // -- Mark story as viewed when it becomes active --
    useEffect(() => {
        // Use storyId (backend ID) not the composite id
        const storyIdToMark = activeStory?.storyId || activeStory?.id;
        if (!storyIdToMark) return;

        const storyIdStr = String(storyIdToMark);
        if (viewedStoryIds.has(storyIdStr)) return;

        storiesAPI.viewStory(storyIdToMark)
            .then(() => {
                // Use storyId (backend ID) consistently
                setViewedStoryIds(prev => new Set([...prev, storyIdStr]));
            })
            .catch(err => {
                console.error('Failed to mark story as viewed:', err);
            });
    }, [activeStory?.id, activeStory?.storyId, viewedStoryIds]);

    // -- Fetch real-time likes/stats when story becomes active --
    useEffect(() => {
        const storyIdToFetch = activeStory?.storyId || activeStory?.id;
        if (!storyIdToFetch) return;

        storiesAPI.getStory(storyIdToFetch)
            .then((res) => {
                const fetchedStory = res.data?.data;
                if (fetchedStory) {
                    setLikedStories(prev => ({ ...prev, [storyIdToFetch]: fetchedStory.has_reacted }));
                    setReactionCounts(prev => ({ ...prev, [storyIdToFetch]: fetchedStory.reactions_count }));
                    setViewCounts(prev => ({ ...prev, [storyIdToFetch]: fetchedStory.views_count || fetchedStory.viewers_count || 0 }));
                }
            })
            .catch(err => {
                console.error('Failed to fetch real-time story stats:', err);
            });
    }, [activeStory?.id, activeStory?.storyId]);

    const handleLikeToggle = async (e) => {
        e.stopPropagation();
        if (!activeStory) return;

        const storyId = activeStory.storyId || activeStory.id;
        const isCurrentlyLiked = likedStories[storyId] !== undefined ? likedStories[storyId] : activeStory.has_reacted;
        const currentCount = reactionCounts[storyId] !== undefined ? reactionCounts[storyId] : (activeStory.reactions_count || 0);

        const newIsLiked = !isCurrentlyLiked;
        const newCount = newIsLiked ? currentCount + 1 : Math.max(0, currentCount - 1);

        setLikedStories(prev => ({ ...prev, [storyId]: newIsLiked }));
        setReactionCounts(prev => ({ ...prev, [storyId]: newCount }));

        try {
            await storiesAPI.reactToStory(storyId, 'like');
        } catch (error) {
            console.error('Failed to react to story:', error);
            // Revert optimistic update on failure
            setLikedStories(prev => ({ ...prev, [storyId]: isCurrentlyLiked }));
            setReactionCounts(prev => ({ ...prev, [storyId]: currentCount }));
        }
    };

    // Helper function to find next group with unviewed stories
    const findNextGroupWithUnviewedStories = useCallback((startGroupIndex) => {
        // Check next groups for any unviewed stories
        for (let g = startGroupIndex + 1; g < groupedStories.length; g++) {
            const groupStories = groupedStories[g].stories;
            for (let s = 0; s < groupStories.length; s++) {
                const story = groupStories[s];
                const storyId = story.id || story.storyId;
                const isViewed = story.has_viewed || story.is_viewed || viewedStoryIds.has(storyId);
                if (!isViewed) {
                    // Found a group with unviewed stories - return first story of that group
                    return { groupIndex: g, storyIndex: 0 };
                }
            }
        }

        return null; // No more groups with unviewed stories
    }, [groupedStories, viewedStoryIds]);

    // -- Navigation Logic --
    const nextStory = useCallback(() => {
        if (!activeGroup) return;

        // First, check if there are more stories in the current user's group
        if (currentStoryIndex < activeGroup.stories.length - 1) {
            // Move to next story in same group (show all stories from current user, even if viewed)
            setCurrentStoryIndex(prev => prev + 1);
            setProgress(0);
            setVideoDuration(5000);
            setIsMediaLoaded(false);
        } else {
            // Finished all stories from current user - look for next user with unviewed stories
            const nextGroupWithUnviewed = findNextGroupWithUnviewedStories(currentGroupIndex);

            if (nextGroupWithUnviewed) {
                // Found a user group with unviewed stories - navigate to it
                setCurrentGroupIndex(nextGroupWithUnviewed.groupIndex);
                setCurrentStoryIndex(nextGroupWithUnviewed.storyIndex);
                setProgress(0);
                setVideoDuration(5000);
                setIsMediaLoaded(false);
            } else {
                // No more users with unviewed stories - close the modal
                if (onStoryViewed && viewedStoryIds.size > 0) {
                    onStoryViewed(Array.from(viewedStoryIds));
                }
                onClose();
            }
        }
    }, [activeGroup, currentStoryIndex, currentGroupIndex, groupedStories.length, findNextGroupWithUnviewedStories, onClose, onStoryViewed, viewedStoryIds]);

    const prevStory = useCallback(() => {
        if (currentStoryIndex > 0) {
            // Prev in same group
            setCurrentStoryIndex(prev => prev - 1);
            setProgress(0);
            setVideoDuration(5000);
            setIsMediaLoaded(false);
        } else if (currentGroupIndex > 0) {
            // Prev group (go to last story of prev group)
            const prevGroup = groupedStories[currentGroupIndex - 1];
            setCurrentGroupIndex(prev => prev - 1);
            setCurrentStoryIndex(prevGroup.stories.length - 1);
            setProgress(0);
            setVideoDuration(5000);
            setIsMediaLoaded(false);
        } // else stay at start
    }, [currentStoryIndex, currentGroupIndex, groupedStories]);

    // Navigate to next user (group)
    const nextUser = useCallback(() => {
        const nextGroupWithUnviewed = findNextGroupWithUnviewedStories(currentGroupIndex);

        if (nextGroupWithUnviewed) {
            // Found a user group with unviewed stories - navigate to it
            setCurrentGroupIndex(nextGroupWithUnviewed.groupIndex);
            setCurrentStoryIndex(0); // Start at first story of the user
            setProgress(0);
            setVideoDuration(5000);
            setIsMediaLoaded(false);
        }
    }, [currentGroupIndex, findNextGroupWithUnviewedStories]);

    // Navigate to previous user (group)
    const prevUser = useCallback(() => {
        if (currentGroupIndex > 0) {
            // Go to previous group (last story of that group)
            const prevGroup = groupedStories[currentGroupIndex - 1];
            setCurrentGroupIndex(prev => prev - 1);
            setCurrentStoryIndex(prevGroup.stories.length - 1);
            setProgress(0);
            setVideoDuration(5000);
            setIsMediaLoaded(false);
        }
    }, [currentGroupIndex, groupedStories]);

    // Check if there are next/previous users available
    const hasNextUser = useMemo(() => {
        return findNextGroupWithUnviewedStories(currentGroupIndex) !== null;
    }, [currentGroupIndex, findNextGroupWithUnviewedStories]);

    const hasPrevUser = useMemo(() => {
        return currentGroupIndex > 0;
    }, [currentGroupIndex]);

    // -- Safety Index Correction --
    // Clamping indices if stories list changes (e.g. after deletion)
    useEffect(() => {
        if (groupedStories.length === 0) return;

        if (currentGroupIndex >= groupedStories.length) {
            setCurrentGroupIndex(groupedStories.length - 1);
            setCurrentStoryIndex(0);
        } else {
            const group = groupedStories[currentGroupIndex];
            if (currentStoryIndex >= group.stories.length) {
                setCurrentStoryIndex(Math.max(0, group.stories.length - 1));
            }
        }
    }, [groupedStories, currentGroupIndex, currentStoryIndex]);


    // -- Timer Logic --
    useEffect(() => {
        if (progressInterval.current) clearInterval(progressInterval.current);

        // Wait for media to load before starting timer
        if (isPaused || showDeleteConfirm || showReportModal || showViewersPanel || showReactionsPanel || !isMediaLoaded) return;

        // Ensure duration is valid
        const duration = (videoDuration && !isNaN(videoDuration) && videoDuration > 0) ? videoDuration : 5000;
        const intervalMs = 100;
        const step = 100 / (duration / intervalMs);


        progressInterval.current = setInterval(() => {
            setProgress(prev => {
                if (prev >= 100) {
                    nextStory();
                    return 0;
                }
                return prev + step;
            });
        }, intervalMs);

        return () => clearInterval(progressInterval.current);
    }, [isPaused, videoDuration, nextStory, showDeleteConfirm, showReportModal, showViewersPanel, showReactionsPanel, isMediaLoaded]);

    // -- Handlers --
    const handleMouseDown = () => {
        setIsPressed(true);
        pressStartTime.current = Date.now();
        setIsPaused(true);
    };

    const handleMouseUp = (e) => {
        // Prevent default to stop mouse emulation if it's a touch event
        // But we need to be careful not to block legit clicks if we mix them.
        // Better strategy: Check event type or use a flag.

        setIsPressed(false);
        setIsPaused(false);

        const pressDuration = Date.now() - pressStartTime.current;
        if (pressDuration < 200) {
            // Its a click, not a hold
            const rect = storyContainerRef.current.getBoundingClientRect();

            // Handle both MouseEvent (clientX) and TouchEvent (changedTouches)
            let clientX;
            if (e.changedTouches && e.changedTouches.length > 0) {
                clientX = e.changedTouches[0].clientX;
            } else {
                clientX = e.clientX;
            }

            const x = clientX - rect.left;

            if (x < rect.width / 3) {
                prevStory();
            } else {
                nextStory();
            }
        }
    };

    // Separate touch end handler to prevent ghost clicks
    const handleTouchEnd = (e) => {
        e.preventDefault(); // Prevents mouse emulation
        handleMouseUp(e);
    };

    const handleDelete = async (e) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }

        // Use the original story ID (storyId) if available, otherwise fallback to id
        // SocialFeed generates composite IDs (id-mediaId) for stories with multiple media
        const targetId = activeStory?.storyId || activeStory?.id;


        if (!targetId) {
            alert("Error: Cannot find story ID to delete");
            return;
        }

        try {
            setIsDeleting(true);
            await storiesAPI.deleteStory(targetId);

            // Check if this is the last story in the entire set
            const isLastStoryOverall = (currentGroupIndex === groupedStories.length - 1) &&
                (currentStoryIndex === activeGroup.stories.length - 1);

            if (onDeleteSuccess) {
                onDeleteSuccess(targetId);
            }

            if (isLastStoryOverall) {
                // Notify parent of viewed stories before closing
                if (onStoryViewed && viewedStoryIds.size > 0) {
                    onStoryViewed(Array.from(viewedStoryIds));
                }
                onClose();
            } else {
                // Navigate to next and then clear deleting state
                setShowDeleteConfirm(false);
                setIsPaused(false);
                nextStory();
                // We keep isDeleting true for a tiny bit to allow indices to stabilize
                setTimeout(() => setIsDeleting(false), 300);
            }
        } catch (err) {
            setIsDeleting(false);
            console.error("API delete failed:", err);
            alert("Failed to delete: " + (err.response?.data?.message || err.message));
            setShowDeleteConfirm(false);
        }
    };


    // If activeStory is missing or we are in a transition, show a loading/placeholder 
    // instead of returning null (which closes the portal/modal)
    if (!activeStory || isDeleting) {
        return (
            <div style={{
                width: '100%',
                height: '100%',
                backgroundColor: '#000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontFamily: '"Red Hat Display", sans-serif'
            }}>
                <div style={{
                    width: '40px',
                    height: '40px',
                    border: '3px solid rgba(255,255,255,0.3)',
                    borderTopColor: '#fff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                }} />
                <style>{`
                    @keyframes spin {
                        to { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            <div
                ref={storyContainerRef}
                style={{
                    width: '100%',
                    height: '100%',
                    position: 'relative',
                    backgroundColor: 'black',
                    overflow: 'hidden',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    WebkitUserDrag: 'none'
                }}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onTouchStart={handleMouseDown}
                onTouchEnd={handleTouchEnd} // Unified handler with preventDefault
                onDragStart={(e) => e.preventDefault()}
            >
                {/* Media */}
                <StoryMedia
                    story={activeStory}
                    isActive={true}
                    isPaused={isPaused || showDeleteConfirm || showReportModal || showViewersPanel || showReactionsPanel}
                    isMuted={isMuted}
                    onVideoDuration={(duration) => setVideoDuration(duration)}
                    onVideoEnded={nextStory}
                    onMediaLoad={() => {
                        setIsMediaLoaded(true);
                    }}
                />

                {/* Progress Bars */}
                <StoryProgress
                    stories={activeGroup.stories}
                    currentIndex={currentStoryIndex}
                    currentProgress={progress}
                />

                {/* Header */}
                <StoryHeader
                    user={activeStory.user}
                    timestamp={activeStory.timestamp} // Assuming formatted string or parseable date
                    onClose={() => {
                        // Notify parent of viewed stories before closing
                        if (onStoryViewed && viewedStoryIds.size > 0) {
                            onStoryViewed(Array.from(viewedStoryIds));
                        }
                        onClose();
                    }}
                    onDelete={() => {
                        setShowDeleteConfirm(true);
                        setIsPaused(true);
                    }}
                    onReport={() => {
                        setShowReportModal(true);
                        setIsPaused(true);
                    }}
                    isOwnStory={activeStory.user?.id === currentUser?.id}
                    isMuted={isMuted}
                    onToggleMute={() => setIsMuted(prev => !prev)}
                    hasReported={activeStory.has_reported}
                    t={t}
                />

                {/* Delete Confirmation Overlay */}
                {showDeleteConfirm && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 0, left: 0, right: 0, bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.8)',
                            zIndex: 2000,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '20px'
                        }}
                        onMouseDown={e => e.stopPropagation()}
                        onMouseUp={e => e.stopPropagation()}
                        onClick={e => e.stopPropagation()}
                    >
                        <h3 style={{ color: 'white', fontFamily: '"Red Hat Display", sans-serif' }}>{t("common.deleteConfirm") || "Delete this story?"}</h3>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowDeleteConfirm(false);
                                    setIsPaused(false);
                                }}
                                style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer' }}
                            >
                                {t("common.cancel") || "Cancel"}
                            </button>
                            <button
                                onClick={handleDelete}
                                style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', backgroundColor: '#ff4d4f', color: 'white', cursor: 'pointer' }}
                            >
                                {t("common.delete") || "Delete"}
                            </button>
                        </div>
                    </div>
                )}

                {/* Preload Next Story */}
                {/* Preload Stories (Prev, Next, Next+1) */}
                {(() => {
                    const preloadList = [];

                    // Helper to get story at relative offset
                    const getRelativeStory = (offset) => {
                        // Flatten stories for easier calc (simplified approach given current structure)
                        // A robust lookup is complex with groups, so we stick to simple neighbor checks

                        // 1. Check Prev
                        if (offset === -1) {
                            if (currentStoryIndex > 0) return activeGroup.stories[currentStoryIndex - 1];
                            if (currentGroupIndex > 0) {
                                const prevGroup = groupedStories[currentGroupIndex - 1];
                                return prevGroup.stories[prevGroup.stories.length - 1];
                            }
                        }

                        // 2. Check Next
                        if (offset === 1) {
                            if (currentStoryIndex < activeGroup.stories.length - 1) return activeGroup.stories[currentStoryIndex + 1];
                            if (currentGroupIndex < groupedStories.length - 1) return groupedStories[currentGroupIndex + 1].stories[0];
                        }

                        // 3. Check Next Next (approximate)
                        if (offset === 2) {
                            // If curr+1 is in same group
                            if (currentStoryIndex < activeGroup.stories.length - 2) return activeGroup.stories[currentStoryIndex + 2];
                            // If curr+1 is last in group, next+2 is first of next group
                            if (currentStoryIndex === activeGroup.stories.length - 2 && currentGroupIndex < groupedStories.length - 1) return groupedStories[currentGroupIndex + 1].stories[0];
                            // If curr is last in group, next is first of next group, next+2 is second of next group
                            if (currentStoryIndex === activeGroup.stories.length - 1 && currentGroupIndex < groupedStories.length - 1) {
                                const nextGroup = groupedStories[currentGroupIndex + 1];
                                if (nextGroup.stories.length > 1) return nextGroup.stories[1];
                                // If next group has only 1, we need next-next group
                                if (currentGroupIndex < groupedStories.length - 2) return groupedStories[currentGroupIndex + 2].stories[0];
                            }
                        }
                        return null;
                    };

                    const prevStory = getRelativeStory(-1);
                    const nextStory = getRelativeStory(1);
                    const nextNextStory = getRelativeStory(2);

                    if (prevStory) preloadList.push(prevStory);
                    if (nextStory) preloadList.push(nextStory);
                    if (nextNextStory) preloadList.push(nextNextStory);

                    return preloadList.map((s, i) => (
                        <StoryMedia key={`preload-${s.id}-${i}`} story={s} preload={true} />
                    ));
                })()}

                {/* Report Modal Integration */}
                {showReportModal && (
                    <div
                        style={{ position: 'absolute', zIndex: 2000 }}
                        onMouseDown={e => e.stopPropagation()}
                    >
                        <ReportModal
                            isOpen={showReportModal}
                            onClose={() => { setShowReportModal(false); setIsPaused(false); }}
                            reportableType="App\Models\Story"
                            reportableId={activeStory.id}
                            post={activeStory}
                        />
                    </div>
                )}

                {/* Bottom Action HUD */}
                {(!showDeleteConfirm && !showReportModal) && (
                    <>
                        {/* Left Side: Like/Reactions Button */}
                        {(() => {
                            const isOwnStory = activeStory.user?.id === currentUser?.id;
                            const storyId = activeStory.storyId || activeStory.id;
                            const isCurrentlyLiked = likedStories[storyId] !== undefined ? likedStories[storyId] : activeStory.has_reacted;

                            return (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (isOwnStory) {
                                            setShowReactionsPanel(true);
                                        } else {
                                            handleLikeToggle(e);
                                        }
                                    }}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onMouseUp={(e) => e.stopPropagation()}
                                    onTouchStart={(e) => e.stopPropagation()}
                                    onTouchEnd={(e) => {
                                        e.stopPropagation();
                                        if (isOwnStory) {
                                            setShowReactionsPanel(true);
                                        } else {
                                            handleLikeToggle(e);
                                        }
                                    }}
                                    style={{
                                        position: 'absolute',
                                        bottom: '24px',
                                        left: '20px',
                                        zIndex: 100,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: isOwnStory ? 'auto' : '44px',
                                        height: '44px',
                                        borderRadius: '22px',
                                        padding: isOwnStory ? '0 16px' : '0',
                                        border: isCurrentlyLiked ? '1.5px solid rgba(255, 65, 108, 0.8)' : '1.5px solid rgba(255,255,255,0.35)',
                                        backgroundColor: isCurrentlyLiked ? 'rgba(255, 65, 108, 0.2)' : 'rgba(0,0,0,0.45)',
                                        backdropFilter: 'blur(10px)',
                                        WebkitBackdropFilter: 'blur(10px)',
                                        color: isCurrentlyLiked ? '#ff416c' : '#fff',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        gap: isOwnStory ? '8px' : '0'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = isCurrentlyLiked ? 'rgba(255, 65, 108, 0.3)' : 'rgba(0,0,0,0.65)';
                                        e.currentTarget.style.borderColor = isCurrentlyLiked ? 'rgba(255, 65, 108, 1)' : 'rgba(255,255,255,0.6)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = isCurrentlyLiked ? 'rgba(255, 65, 108, 0.2)' : 'rgba(0,0,0,0.45)';
                                        e.currentTarget.style.borderColor = isCurrentlyLiked ? 'rgba(255, 65, 108, 0.8)' : 'rgba(255,255,255,0.35)';
                                    }}
                                    aria-label={isOwnStory ? "View reactions" : (isCurrentlyLiked ? "Unlike story" : "Like story")}
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24"
                                        fill={isCurrentlyLiked ? "currentColor" : "none"}
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round">
                                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                                    </svg>
                                    {isOwnStory && (
                                        <span style={{ fontSize: '15px', fontWeight: '700', fontFamily: 'inherit' }}>
                                            {reactionCounts[storyId] || 0}
                                        </span>
                                    )}
                                </button>
                            );
                        })()}

                        {/* Right Side: Eye Icon Button (Author Only) */}
                        {activeStory.user?.id === currentUser?.id && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowViewersPanel(true);
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                onMouseUp={(e) => e.stopPropagation()}
                                onTouchStart={(e) => e.stopPropagation()}
                                onTouchEnd={(e) => { e.stopPropagation(); setShowViewersPanel(true); }}
                                style={{
                                    position: 'absolute',
                                    bottom: '24px',
                                    right: '20px',
                                    zIndex: 100,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 'auto',
                                    height: '44px',
                                    borderRadius: '22px',
                                    padding: '0 16px',
                                    border: '1.5px solid rgba(255,255,255,0.35)',
                                    backgroundColor: 'rgba(0,0,0,0.45)',
                                    backdropFilter: 'blur(10px)',
                                    WebkitBackdropFilter: 'blur(10px)',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    gap: '8px'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.65)';
                                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.6)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.45)';
                                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.35)';
                                }}
                                aria-label="View story viewers"
                            >
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" strokeWidth="2"
                                    strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                    <circle cx="12" cy="12" r="3" />
                                </svg>
                                <span style={{ fontSize: '15px', fontWeight: '700', fontFamily: 'inherit' }}>
                                    {viewCounts[activeStory.storyId || activeStory.id] || 0}
                                </span>
                            </button>
                        )}
                    </>
                )}

                {/* Story Viewers Panel – moved outside overflow:hidden, rendered in parent wrapper */}

                {/* Navigation Arrows - Navigate between users */}
                {hasPrevUser && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            prevUser();
                        }}
                        style={{
                            position: 'absolute',
                            left: '20px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 100,
                            width: '48px',
                            height: '48px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            border: '2px solid rgba(255, 255, 255, 0.8)',
                            color: 'white',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            backdropFilter: 'blur(10px)',
                            padding: 0
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 1)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.8)';
                        }}
                        aria-label="Previous user"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                )}

                {hasNextUser && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            nextUser();
                        }}
                        style={{
                            position: 'absolute',
                            right: '20px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 100,
                            width: '48px',
                            height: '48px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            border: '2px solid rgba(255, 255, 255, 0.8)',
                            color: 'white',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            backdropFilter: 'blur(10px)',
                            padding: 0
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 1)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.8)';
                        }}
                        aria-label="Next user"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                )}
            </div>

            {/* Story Viewers & Reactions Panels */}
            <StoryReactionsPanel
                storyId={activeStory?.storyId || activeStory?.id}
                isOpen={showReactionsPanel}
                onClose={() => setShowReactionsPanel(false)}
                currentUserId={currentUser?.id}
                onNavigate={() => {
                    setShowReactionsPanel(false);
                    onClose?.();
                }}
            />

            <StoryViewersPanel
                storyId={activeStory?.storyId || activeStory?.id}
                isOpen={showViewersPanel}
                onClose={() => setShowViewersPanel(false)}
                currentUserId={currentUser?.id}
                onNavigate={() => {
                    setShowViewersPanel(false);
                    onClose?.();
                }}
            />
        </div>
    );
};

export default StoryViewer;
